import React, { forwardRef, useMemo, Fragment } from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'

import { transformLineItems } from 'utils/LineItems/transformLineItems'

const LineItems = forwardRef(({ order, id }, ref) => {
  const { lineItems, subtotal, promotions, tip, total } = order

  const transformedLineItems = transformLineItems(lineItems)

  const lineItemsWithSpecialType = lineItems?.filter(
    (item) => !isEmpty(item.itemSpecialType)
  )
  const lineItemsWithSpecialTypeWithoutTax = lineItemsWithSpecialType?.filter(
    ({ name }) => name !== 'Tax'
  )

  const discount =
    useMemo(() => {
      return promotions?.reduce((res, promotion) => {
        return res + promotion.discountAmountInCents * 0.01
      }, 0)
    }, [promotions]) ?? 0

  const lineItemsWithSpecialTypeTotalPrice =
    useMemo(() => {
      return lineItemsWithSpecialTypeWithoutTax?.reduce((res, { price }) => {
        return res + Number(price)
      }, 0)
    }, [lineItemsWithSpecialTypeWithoutTax]) ?? 0

  const itemSubtotal = useMemo(() => {
    return (
      Number(subtotal) +
      Math.abs(discount) -
      lineItemsWithSpecialTypeTotalPrice
    ).toFixed(2)
    //eslint-disable-next-line
  }, [subtotal, discount, lineItemsWithSpecialType])

  return (
    <section className="order-details__section" ref={ref}>
      <header className="order-details__header" id={id}>
        <h4 className="order-details__main-header">Line items</h4>
        <h6 className="order-details__sub-header">
          Refunded items do not show in the line items below
        </h6>
      </header>
      {transformedLineItems?.map(
        ({ name, price, modifiers, uuid, quantity }) => {
          return (
            <Fragment key={uuid}>
              <div className="order-details__row">
                <span>
                  ({quantity}) {name}
                </span>
                <span>${price}</span>
              </div>
              {!isEmpty(modifiers) &&
                modifiers?.map((modifier) => {
                  if (!isEmpty(modifier)) {
                    return (
                      <div
                        className="order-details__row -nested"
                        key={modifier.uuid}
                      >
                        <span>
                          + ({modifier.quantity}) {modifier.name}
                        </span>
                        <span>${modifier.price}</span>
                      </div>
                    )
                  } else {
                    return null
                  }
                })}
            </Fragment>
          )
        }
      )}
      <div className="order-details__row -bold">
        <span>Item Subtotal</span>
        <span>${itemSubtotal}</span>
      </div>
      {!isEmpty(promotions) && (
        <div className="order-details__row -discount -italic">
          <span>Discount</span>
          <span>- ${Math.abs(discount).toFixed(2)}</span>
        </div>
      )}
      {lineItemsWithSpecialType?.map(({ name, price }, index) => {
        return (
          <div className="order-details__row -italic" key={index}>
            <span>{name}</span>
            <span>${price}</span>
          </div>
        )
      })}
      {!isEmpty(order?.tip) && (
        <div className="order-details__row -italic">
          <span>Gratuity</span>
          <span>${tip}</span>
        </div>
      )}
      <div className="order-details__row -bold">
        <span>Total</span>
        <span>${total}</span>
      </div>
    </section>
  )
})

LineItems.displayName = 'LineItems'

LineItems.propTypes = {
  order: PropTypes.object,
  id: PropTypes.string,
}

export default LineItems
